import React from 'react'
import { Positioner } from 'components/Positioner'
import { Typography } from 'components/Typography'
import * as base from 'components/HeroSection/HeroSection.module.scss'
import * as theme from 'components/HeroSection/HeroSection-theme.module.scss'
import { Link } from 'gatsby'
import { useMediaQuery } from 'hooks/useMediaQuery'

let styles = {}
Object.assign(styles, base, theme)

const Eyebrow = Typography
const Title = Typography
const SubTitle = Typography

const CTA = ({
  href = '',
  target = '',
  'data-cy': dataCy = '',
  className = null,
  children,
}) => {
  return (
    <Link data-cy={dataCy} to={href} target={target} className={className}>
      {children}
    </Link>
  )
}

export const HeroSection = ({
  backgroundColor = null,
  backgroundImage = '',
  contentPosition = 'left', // 'left' or 'right'
  cta,
  ctaJustify = 'left', // 'left', 'center', 'right'
  ctaUppercase = true,
  ctaClasses = '',
  ctaUrl,
  eyebrow,
  eyebrowClasses = '',
  imageAlign = 'top',
  imageFit = 'cover', // 'cover', 'contain'
  imageJustify = 'left', // 'top', 'center', 'bottom'
  imagePosition = 'left', // 'left' or 'right'
  imageUrl = '',
  innerContentClass = '',
  mobileContentOrder: mobileContentOrderProp = 1,
  mobileImageOrder: mobileImageOrderProp = 2,
  preserveImageRatio = true,
  positionerClasses = '',
  rootClassName = '',
  subtitle = '',
  subtitleClasses = '',
  title,
  titleClasses = '',
  variant = 'stacked', // 'stacked' or 'column'
}) => {
  const isMobile = useMediaQuery('(max-width: 599px)')

  // Determine CSS classes based on variant and positioning
  const rootClasses = `${styles.root} ${styles[variant]} ${styles[contentPosition]} ${rootClassName}`

  // For desktop, maintain the order based on `imagePosition`, for mobile, adjust it accordingly

  const mobileContentOrder = isMobile
    ? mobileContentOrderProp
    : imagePosition === 'left'
    ? 1
    : 2
  const mobileImageOrder = isMobile
    ? mobileImageOrderProp
    : imagePosition === 'left'
    ? 2
    : 1

  const contentOrder = isMobile
    ? mobileContentOrder
    : imagePosition === 'left'
    ? 2
    : 1

  const imageOrder = isMobile
    ? mobileImageOrder
    : imagePosition === 'left'
    ? 1
    : 2

  let backgroundImageUrl = imageUrl
  if (backgroundImage) {
    backgroundImageUrl = require(`images/${backgroundImage}`).default
  }

  const imageClasses = [
    styles.image,
    styles[imageAlign],
    styles[imageFit],
    styles[imageJustify],
    preserveImageRatio ? styles['preserve-ratio'] : '',
  ]
    .filter(Boolean)
    .join(' ')

  const ctaComponentClasses = [
    styles.cta,
    styles[ctaUppercase ? 'uppercase' : ''],
    styles[ctaJustify],
    ctaClasses,
  ]
    .filter(Boolean)
    .join(' ')

  const positionerComponentClass = [
    styles.positioner,
    styles[contentPosition],
    styles[variant === 'column' ? 'contentPadding' : ''],
    positionerClasses,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={rootClasses}
      style={
        variant === 'stacked'
          ? {
              backgroundImage: `url(${backgroundImageUrl})`,
              ...(backgroundColor && { backgroundColor }),
            }
          : { ...(backgroundColor && { backgroundColor }) }
      }
    >
      {variant === 'column' && (
        <img
          className={imageClasses}
          src={backgroundImageUrl}
          style={{ order: imageOrder }}
          alt=""
        />
      )}
      <Positioner
        data-cy="HeroSection-Positioner"
        className={`${styles.positioner} ${positionerComponentClass}`}
        style={{ order: contentOrder }}
      >
        <div
          data-cy="HeroSection-InnerContent"
          className={`${styles.innerContent} ${innerContentClass}`}
        >
          {eyebrow && (
            <Eyebrow
              data-cy={'HeroSection-Eyebrow'}
              className={`${styles.eyebrow} ${eyebrowClasses}`}
            >
              {eyebrow}
            </Eyebrow>
          )}
          {title && (
            <Title
              data-cy={'HeroSection-Title'}
              tag="h3"
              className={`${styles.title} ${titleClasses}`}
            >
              {title}
            </Title>
          )}
          {subtitle && (
            <SubTitle
              data-cy={'HeroSection-Subtitle'}
              tag="p"
              className={`${styles.subtitle} ${subtitleClasses}`}
            >
              {subtitle}
            </SubTitle>
          )}
          {cta && (
            <CTA
              data-cy={'HeroSection-CTA'}
              href={ctaUrl}
              className={ctaComponentClasses}
            >
              {cta}
            </CTA>
          )}
        </div>
      </Positioner>
    </div>
  )
}

export default HeroSection
